// Safety escalation, shown at the top of Today (brief §9).
//
// Three things matter here and none of them are colour: the level is stated in
// words, carries an icon, and urgent flags cannot be dismissed with a tap the way a
// "worth mentioning" one can — acknowledging an urgent flag asks for a deliberate
// confirmation, because the failure mode is someone swiping it away by reflex.

import { useState } from 'react';
import { api, type RedFlag } from '../lib/api.ts';
import { Button } from './ui.tsx';

export function RedFlagBanner({ flags, onChange }: { flags: RedFlag[]; onChange: () => void }) {
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const acknowledge = async (id: string): Promise<void> => {
    setBusy(true);
    try {
      await api.medical.acknowledgeRedFlag(id);
      setConfirming(null);
      onChange();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      {flags.map((flag) => {
        const urgent = flag.level === 'urgent';
        return (
          <div
            key={flag.id}
            role="alert"
            className={`rounded-xl border p-3.5 ${
              urgent
                ? 'bg-critical/10 border-critical/50'
                : 'bg-warning/10 border-warning/50'
            }`}
          >
            <p className="flex items-center gap-2 text-sm font-semibold">
              <span aria-hidden="true">{urgent ? '⚠' : 'ⓘ'}</span>
              {urgent ? 'Seek urgent medical attention' : 'Worth discussing with a doctor'}
            </p>
            <p className="text-sm mt-1.5 leading-relaxed">{flag.message}</p>

            <div className="mt-3">
              {confirming === flag.id ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-ink-2">
                    {urgent ? 'Only dismiss this once you have sought advice.' : 'Dismiss this note?'}
                  </span>
                  <Button variant="secondary" disabled={busy} onClick={() => void acknowledge(flag.id)}>
                    Yes, dismiss
                  </Button>
                  <Button variant="ghost" disabled={busy} onClick={() => setConfirming(null)}>Keep</Button>
                </div>
              ) : (
                <Button variant="ghost" onClick={() => setConfirming(flag.id)}>Dismiss</Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
